import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, finalize } from 'rxjs';
import { Httpservice } from '../../../services/httpservice';
import { ApiMethod, AuthEndPoints } from '../../../helper/apiconstant';
import {
  buildPaginationItems,
  buildPaginationLabel,
  buildQueryString,
  canShowPagination,
  extractPagination,
  getApiErrorMessage,
} from '../../../helper/table-utils';
import { DashboardHeaderComponent } from '../../structure/header/header';
import { DashboardSidebarComponent } from '../../structure/sidebar/sidebar';
import { DEFAULT_DASHBOARD_USER } from '../../dashboard/dashboard.data';
import { ConfigurationPortalItem, ConfigurationPortalPagination } from './configuration-portal.interfaces';

type ConfigurationPortalModalMode = 'create' | 'edit' | 'view';
type ConfigurationPortalQueryValue = string | number | null | undefined;
type ConfigurationPortalQueryParams = Record<string, ConfigurationPortalQueryValue>;

const CONFIGURATION_PORTAL_NO_RECORDS_LABEL = 'No records';
const CONFIGURATION_PORTAL_SHOWING_RESULTS_LABEL = 'Showing';
const CONFIGURATION_PORTAL_RESULTS_LABEL = 'results';
const CONFIGURATION_PORTAL_FAILED_LOAD_MESSAGE = 'Failed to load configuration variables.';

@Component({
  selector: 'app-configuration-portal',
  imports: [CommonModule, ReactiveFormsModule, DashboardSidebarComponent, DashboardHeaderComponent],
  templateUrl: './configuration-portal.html',
  styleUrl: './configuration-portal.scss',
})
export class ConfigurationPortal implements OnInit {
  protected readonly user = DEFAULT_DASHBOARD_USER;
  protected readonly pageSizeOptions = [15, 25, 50, 100];
  protected readonly filterForm: UntypedFormGroup;
  protected readonly portalForm: UntypedFormGroup;
  protected readonly pageSizeControl: FormControl;

  protected sidebarCollapsed = false;
  protected mobileSidebarOpen = false;
  protected isLoading = false;
  protected isSubmitting = false;
  protected isVariableModalOpen = false;
  protected isDeleteModalOpen = false;
  protected modalMode: ConfigurationPortalModalMode = 'create';
  protected selectedSetting: ConfigurationPortalItem | null = null;
  protected formSubmitted = false;

  protected portalSettings: ConfigurationPortalItem[] = [];
  protected pagination: ConfigurationPortalPagination = {
    page_no: 1,
    page_size: 15,
    total_records: 0,
    total_pages: 0,
  };

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor(
    private readonly fb: UntypedFormBuilder,
    protected readonly httpSvc: Httpservice,
  ) {
    this.filterForm = this.fb.group({
      search: [''],
      page_size: [15],
    });

    this.pageSizeControl = this.filterForm.get('page_size') as FormControl;

    this.portalForm = this.fb.group({
      variable_name: ['', [Validators.required, Validators.maxLength(255)]],
      variable_value: ['', [Validators.required, Validators.maxLength(500)]],
      description: ['', [Validators.maxLength(1000)]],
    });
  }

  ngOnInit(): void {
    this.loadConfigurationPortal(1);

    this.filterForm.get('search')?.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.loadConfigurationPortal(1));

    this.filterForm.get('page_size')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadConfigurationPortal(1));
  }

  protected handleMenuToggle(): void {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      return;
    }

    this.mobileSidebarOpen = !this.mobileSidebarOpen;
  }

  protected closeMobileSidebar(): void {
    this.mobileSidebarOpen = false;
  }

  protected clearFilters(): void {
    this.filterForm.reset(
      {
        search: '',
        page_size: this.pageSizeControl.value ?? 15,
      },
      { emitEvent: false },
    );
    this.loadConfigurationPortal(1);
  }

  protected loadConfigurationPortal(pageNo = this.pagination.page_no): void {
    this.isLoading = true;
    const query = this.buildQueryString({
      search: this.filterForm.get('search')?.value,
      page_no: pageNo,
      page_size: this.filterForm.get('page_size')?.value,
    });

    this.httpSvc
      .requestCall(AuthEndPoints.GET_CONFIGURATION_PORTAL_LIST, ApiMethod.GET, query)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          this.portalSettings = this.mapConfigurationPortalItems(response?.value?.portal_settings ?? response?.portal_settings ?? []);
          this.pagination = this.extractPagination(response?.value ?? response) ?? this.pagination;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error(CONFIGURATION_PORTAL_FAILED_LOAD_MESSAGE, error);
          this.portalSettings = [];
          this.pagination = {
            page_no: pageNo,
            page_size: Number(this.filterForm.get('page_size')?.value ?? 10),
            total_records: 0,
            total_pages: 0,
          };
          this.cdr.markForCheck();
        },
      });
  }

  protected openAddVariableModal(): void {
    this.modalMode = 'create';
    this.selectedSetting = null;
    this.portalForm.enable({ emitEvent: false });
    this.formSubmitted = false;
    this.portalForm.reset({
      variable_name: '',
      variable_value: '',
      description: '',
    });
    this.isVariableModalOpen = true;
  }

  protected openEditVariableModal(setting: ConfigurationPortalItem): void {
    this.modalMode = 'edit';
    this.selectedSetting = setting;
    this.portalForm.enable({ emitEvent: false });
    this.formSubmitted = false;
    this.portalForm.reset({
      variable_name: setting.variable_name,
      variable_value: setting.variable_value,
      description: setting.description ?? '',
    });
    this.isVariableModalOpen = true;
  }

  protected openViewVariableModal(setting: ConfigurationPortalItem): void {
    this.modalMode = 'view';
    this.selectedSetting = setting;
    this.portalForm.enable({ emitEvent: false });
    this.formSubmitted = false;
    this.portalForm.reset({
      variable_name: setting.variable_name,
      variable_value: setting.variable_value,
      description: setting.description ?? '',
    });
    this.isVariableModalOpen = true;
    this.portalForm.disable({ emitEvent: false });
  }

  protected closeVariableModal(): void {
    this.isVariableModalOpen = false;
    this.isSubmitting = false;
    this.modalMode = 'create';
    this.selectedSetting = null;
    this.formSubmitted = false;
    this.portalForm.enable({ emitEvent: false });
    this.portalForm.reset({
      variable_name: '',
      variable_value: '',
      description: '',
    });
  }

  protected openDeleteVariableModal(setting: ConfigurationPortalItem): void {
    this.selectedSetting = setting;
    this.isDeleteModalOpen = true;
  }

  protected closeDeleteModal(): void {
    this.isDeleteModalOpen = false;
    this.selectedSetting = null;
  }

  protected saveVariable(): void {
    if (this.modalMode === 'view') {
      return;
    }

    if (this.portalForm.invalid || this.isSubmitting) {
      this.formSubmitted = true;
      this.portalForm.markAllAsTouched();
      return;
    }

    const payload = {
      variable_name: this.getTrimmedControlValue('variable_name'),
      variable_value: this.getTrimmedControlValue('variable_value'),
      description: this.getOptionalTrimmedControlValue('description'),
    };

    this.isSubmitting = true;

    const request$ = this.modalMode === 'edit' && this.selectedSetting?.id
      ? this.httpSvc.requestCall(
          `${AuthEndPoints.UPDATE_CONFIGURATION_VARIABLE}?variable_id=${this.selectedSetting.id}`,
          ApiMethod.PATCH,
          payload,
        )
      : this.httpSvc.requestCall(AuthEndPoints.ADD_CONFIGURATION_VARIABLE, ApiMethod.POST, payload);

    request$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isSubmitting = false;
        }),
      )
      .subscribe({
        next: () => {
          this.closeVariableModal();
          this.loadConfigurationPortal(this.pagination.page_no);
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error(getApiErrorMessage(error) ?? 'Failed to save configuration variable.', error);
        },
      });
  }

  protected confirmDelete(): void {
    if (!this.selectedSetting?.id || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;

    this.httpSvc
      .requestCall(
        `${AuthEndPoints.DELETE_CONFIGURATION_VARIABLE}?variable_id=${this.selectedSetting.id}`,
        ApiMethod.DELETE,
        '',
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isSubmitting = false;
        }),
      )
      .subscribe({
        next: () => {
          this.closeDeleteModal();
          this.loadConfigurationPortal(this.pagination.page_no);
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error(getApiErrorMessage(error) ?? 'Failed to delete configuration variable.', error);
        },
      });
  }

  protected canShowPagination(): boolean {
    return canShowPagination(this.pagination.total_records);
  }

  protected getPaginationLabel(): string {
    return buildPaginationLabel(
      this.pagination,
      CONFIGURATION_PORTAL_NO_RECORDS_LABEL,
      CONFIGURATION_PORTAL_SHOWING_RESULTS_LABEL,
      CONFIGURATION_PORTAL_RESULTS_LABEL,
    );
  }

  protected getPaginationItems(): number[] {
    return buildPaginationItems(this.pagination.total_pages, this.pagination.page_no);
  }

  protected goToPreviousPage(): void {
    this.goToPage(this.pagination.page_no - 1);
  }

  protected goToNextPage(): void {
    this.goToPage(this.pagination.page_no + 1);
  }

  protected goToPage(pageNo: number): void {
    if (pageNo < 1 || pageNo > this.pagination.total_pages) {
      return;
    }

    this.loadConfigurationPortal(pageNo);
  }

  protected isRequiredFieldInvalid(controlName: string): boolean {
    const control = this.portalForm.get(controlName);
    return Boolean(control?.hasError('required') && (control.touched || this.formSubmitted));
  }

  protected trackBySettingId(_: number, item: ConfigurationPortalItem): number {
    return item.id;
  }

  private buildQueryString(params: ConfigurationPortalQueryParams): string {
    return buildQueryString(params);
  }

  private extractPagination(value: unknown): ConfigurationPortalPagination | null {
    return extractPagination<ConfigurationPortalPagination>(value);
  }

  private mapConfigurationPortalItems(items: unknown): ConfigurationPortalItem[] {
    if (!Array.isArray(items)) {
      return [];
    }

    return items.map((item) => {
      const record = item as Record<string, unknown>;
      return {
        id: Number(record['id'] ?? 0),
        variable_name: typeof record['variable_name'] === 'string' ? record['variable_name'] : 'Untitled variable',
        variable_value: typeof record['variable_value'] === 'string' ? record['variable_value'] : '',
        description: typeof record['description'] === 'string' ? record['description'] : null,
      };
    });
  }

  private getTrimmedControlValue(controlName: string): string {
    const value = this.portalForm.get(controlName)?.value;
    return typeof value === 'string' ? value.trim() : String(value ?? '').trim();
  }

  private getOptionalTrimmedControlValue(controlName: string): string | null {
    const value = this.getTrimmedControlValue(controlName);
    return value || null;
  }
}
